import React, { useState, useMemo } from "react";
import useSWR from "swr";
import { URLS } from "../../services/urls";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import _ from "lodash";

import HomeBanner from "./components/BannerSlider";
import OldJob from "./components/OldJob";
import JobTabContent from "./components/JobTabContent";
import PopCategory from "./components/PopCategory";
import JobsByLocation from "./components/JobsByLocation";
import JobProcess from "./components/JobProcess";
import NewsLetter from "../../components/newsletter";

import { API } from "../../services/axios";
import { getAvailableCategories, hasOpenedJobs } from "../../utils/fonctions";

const fetcher = (url) =>
  API.get(url).then((res) => {
    return res.data.results.filter((el) =>
      [
        "Douala",
        "Yaoundé",
        "Bafoussam",
        "Garoua",
        "Maroua",
        "Bamenda",
        "Buea",
        "Bertoua",
        "Kribi",
      ].includes(el.name)
    );
  });

const HomePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // State
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState(null);
  const [location, setLocation] = useState(null);
  const [showIndustries, setShowIndustries] = useState(false);
  const [showLocation, setShowLocation] = useState(false);

  // Fetch data
  const { data: categories, isLoading: loadingCategories } = useSWR(
    URLS.CONFIGS.PROFILE.SECTOR.list("", 1500)
  );
  const { data: locations, isLoading: loadingLocations } = useSWR(
    URLS.CONFIGS.PROFILE.COUNTRIES.CITIES.cmr(""),
    fetcher
  );

  // Compute categories with open jobs, sorted and limited
  const displayedCategories = useMemo(() => {
    if (!categories?.results) return [];
    return _.orderBy(
      categories.results.filter((cat) => hasOpenedJobs(cat)),
      [(cat) => cat.jobs.length],
      ["desc"]
    ).slice(0, 7);
  }, [categories]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (category?.id) params.set("industry", category.id);
    if (location?.name) params.set("location", location.name);
    navigate(`/search?${params.toString()}`);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    navigate(
      `/search?search=${search}&industry=${category?.id ?? ""}&location=${
        location?.name ?? ""
      }`
    );
  };

  return (
    <>
      {/* Hero & Filters */}
      <section className="section-box mt-95">
        <div className="banner-hero hero-2">
          <div className="banner-inner">
            <div className="block-banner">
              <h1
                className="text-42 color-white wow animate__animated animate__fadeInUp"
                dangerouslySetInnerHTML={{ __html: t("textJobBoard") }}
              ></h1>
              <div
                className="font-lg font-regular color-white mt-20 wow animate__animated animate__fadeInUp"
                data-wow-delay=".1s"
              ></div>
              <div
                className="form-find mt-40 wow animate__animated animate__fadeIn"
                data-wow-delay=".2s"
              >
                <form onSubmit={handleSubmit}>
                  <div className="job_filters_wrap d-md-flex">
                    <div className="box-industry">
                      {showIndustries && (
                        <div className="dropdown" id="dropdown">
                          {categories?.results?.map((cat) => (
                            <a
                              href="#"
                              className={
                                category?.id === cat.id ? "active" : ""
                              }
                              key={cat?.id}
                              onClick={() => {
                                setCategory(cat);
                                setShowIndustries(false);
                                navigate(`/search?industry=${cat?.id ?? ""}`);
                              }}
                            >
                              <i className="bx bx-plus-circle"></i>
                              {cat?.name}
                            </a>
                          ))}
                        </div>
                      )}
                      <label
                        htmlFor="search_category"
                        className="select2 select2-container select2-container--default elect2-container--open"
                        dir="ltr"
                        style={{ width: "158.359375px" }}
                        onClick={() => setShowIndustries(!showIndustries)}
                      >
                        <span className="selection">
                          <span
                            className="select2-selection select2-selection--single"
                            role="combobox"
                            aria-haspopup="true"
                            aria-expanded="false"
                            tabindex="0"
                            aria-labelledby="select2-search_category-56-container"
                          >
                            <span
                              className="select2-selection__rendered"
                              id="select2-search_category-56-container"
                              title="Industry"
                            >
                              {category?.name ?? t("textIndustry")}
                            </span>
                            <span
                              className="select2-selection__arrow"
                              role="presentation"
                            >
                              <b role="presentation"></b>
                            </span>
                          </span>
                        </span>
                        <span
                          className="dropdown-wrapper"
                          aria-hidden="true"
                        ></span>
                      </label>
                    </div>
                    <div className="box-industry">
                      {showLocation && (
                        <div className="dropdown" id="dropdown">
                          {locations?.map((cat) => (
                            <a
                              href="#"
                              className={
                                location?.id === cat.id ? "active" : ""
                              }
                              key={cat?.id}
                              onClick={() => {
                                setLocation(cat);
                                setShowLocation(false);
                                navigate(`/search?location=${cat?.name ?? ""}`);
                              }}
                            >
                              <i className="bx bx-plus-circle"></i>
                              {cat?.name}
                            </a>
                          ))}
                        </div>
                      )}
                      <label
                        className="select2 select2-container select2-container--default card-location"
                        dir="ltr"
                        style={{ width: "91.75px" }}
                        onClick={() => setShowLocation(!showLocation)}
                      >
                        <span className="selection">
                          <span
                            className="select2-selection select2-selection--single "
                            role="combobox"
                            aria-haspopup="true"
                            aria-expanded="false"
                            tabindex="0"
                            aria-labelledby="select2-search_location-container"
                          >
                            <span
                              className="select2-selection__rendered"
                              id="select2-search_location-container"
                              title="Location"
                            >
                              {location?.name ?? t("textLocation")}
                            </span>
                            <span
                              className="select2-selection__arrow"
                              role="presentation"
                            >
                              <b role="presentation"></b>
                            </span>
                          </span>
                        </span>
                        <span
                          className="dropdown-wrapper"
                          aria-hidden="true"
                        ></span>
                      </label>
                    </div>

                    <input
                      className="form-input input-keysearch mr-10"
                      type="text"
                      name="search_keywords"
                      id="search_keywords"
                      placeholder={t("textkeyword")}
                    />

                    <input
                      className="btn btn-default btn-find font-sm"
                      type="submit"
                      value={t("textSearch")}
                    />
                  </div>
                </form>
              </div>
              <div
                className="list-tags-banner mt-20 wow animate__animated animate__fadeInUp"
                data-wow-delay=".3s"
              >
                <strong>{t("textpop")}:</strong>
                <a href="#">{t("textDesigner")}</a>, <a href="#">Web</a>,{" "}
                <a href="#">IOS</a>, <a href="#">{t("textDeveloper")}</a>,{" "}
                <a href="#">PHP</a>, <a href="#">Senior</a>,{" "}
                <a href="#">{t("textEngineer")}</a>
              </div>
            </div>
          </div>
          <div className="mt-60">
            {!loadingCategories && (
              <HomeBanner categories={categories?.results?getAvailableCategories(categories?.results): []} />
            )}
          </div>
        </div>
        {/* <Clients /> */}
      </section>

      {/* All Jobs Slider */}
      <OldJob />

      {/* Day Job Section */}
      <section className="section-box mt-30">
        <div className="container">
          <div className="text-start">
            <h2 className="section-title text mb-10 wow animate__animated animate__fadeInUp">
              {t("dayJob")}
            </h2>
            <p className="font-lg text-danger color-text-paragraph-2 wow animate__animated animate__fadeInUp">
              {t("findJob")}
            </p>
            {loadingCategories && (
              <div className="d-flex">
                <div className="spinner-border m-auto spinner-border-md" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            )}
          </div>

          {/* Tabs Navigation */}
          <div className="list-tabs mt-40">
            <ul className="nav nav-tabs" role="tablist">
              {!loadingCategories &&
                displayedCategories.map((cat, idx) => (
                  <li key={cat.id} className="nav-item">
                    <a
                      className={idx === 0 ? "nav-link active" : "nav-link"}
                      id={`nav-tab-job-${cat.id}`}
                      href={`#tab-job-${cat.id}`}
                      data-bs-toggle="tab"
                      role="tab"
                      aria-controls={`tab-job-${cat.id}`}
                      aria-selected={idx === 0}
                    >
                      {cat.name}
                    </a>
                  </li>
                ))}
            </ul>
          </div>

          {/* Tabs Content */}
          <div className="mt-50">
            <div className="tab-content" id="myTabContent-1">
              {!loadingCategories &&
                displayedCategories.map((cat, idx) => (
                  <div
                    key={cat.id}
                    className={`tab-pane fade ${idx === 0 ? "show active" : ""}`}
                    id={`tab-job-${cat.id}`}
                    role="tabpanel"
                    aria-labelledby={`nav-tab-job-${cat.id}`}
                  >
                    <JobTabContent jobs={cat.jobs} category={cat.id} />
                  </div>
                ))}
            </div>
          </div>
        </div>
      </section>

      {/* Other Sections */}
      <PopCategory categories={categories?.results ?? []} />
      <JobsByLocation locations={locations ?? []} />
      <JobProcess />
      <NewsLetter />
    </>
  );
};

export default HomePage;
